import React, { useRef, useState, DragEvent, ChangeEvent, useEffect } from "react";
import { createFoldername, fetchFiles, fileUpload } from "./action";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthHeader from "src/components/@auth/auth-layout/auth-header";
import AppFooter from "src/layouts/footer";
import PrimaryButton from "src/components/reusable/button/primary-button";
import TrashIcon from "src/components/icons/common/trash";
import { UploadIcon } from "lucide-react";

const FileManager: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileError, setFileError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [dragging, setDragging] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    setFileError(""); 
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles((prev) => [...prev, ...files]);
    setFileError(""); 
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitCreateFolder = () => {
    let hasError = false;

    if (selectedFiles.length === 0) {
      setFileError("Please select at least one file.");
      hasError = true;
    } else {
      setFileError("");
    }

    if (hasError) return;

    setSubmittingAction(true);

    const folderPath = searchParams.get("folder") || "root";

    fileUpload(folderPath, selectedFiles, ({ success, errorType }) => {
      setSubmittingAction(false);

      if (success) {
        setSelectedFiles([]);
        getFolderFiles();
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.success("File uploaded successfully");
      } else {
        toast.error(
          errorType === "upload"
            ? "Failed to upload files. Please try again."
            : "Unexpected error."
        );
      }
    });
  };

  useEffect(() => {
    const accessGranted = localStorage.getItem("dropboxAccess");
    if (accessGranted !== "granted") {
      navigate("/dropbox-login", { replace: true });
    }
  }, []);

  const getFolderFiles = () => {
    const folderPath = searchParams.get("folder") || "root";

    fetchFiles(folderPath, ({ success, data }) => {
      if (success) {
        setItems(data || []);
      }
    });
  };

  useEffect(() => {
    getFolderFiles();
  }, [searchParams]);

  return (
    <>
      <div className="p-6 container mx-auto font-sans min-h-[calc(100vh-88px)]">
        {/* Breadcrumb */}
        <div className="text-lg text-gray-600 mb-2">
          <span
            onClick={() => navigate("/dropbox")}
            className="text-primary-800 cursor-pointer hover:underline"
          >
            File Manager
          </span>
          {searchParams.get("folder") && (
            <>
              <span className="mx-1">{">"}</span>
              <span className="font-medium text-gray-800">{searchParams.get("folder")}</span>
            </>
          )}
        </div>

          <h2 className="text-2xl font-semibold text-primary-800 mb-2">Upload your files in <strong>{searchParams.get("folder")}</strong> folder</h2>
        <div className="flex gap-6 w-full mb-3">
          {/* Left Section */}
          <div className="w-1/2 space-y-4">
          
            <div
              className={`border border-primary-900 rounded-lg h-[280px] flex justify-center items-center cursor-pointer p-10 ${
                dragging ? "bg-gray-200" : ""
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center text-lg">
                <UploadIcon />
                <p className="text-center text-base font-bold font-nunito mt-3">
                  Drag and Drop files to upload
                </p>
                <p className="text-base py-0.5 font-bold font-nunito">or</p>
                <p className="text-primary-900 font-bold underline cursor-pointer hover:text-primary-800 transition duration-300 ease-in-out text-base font-nunito">
                  Browse
                </p>
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              multiple
            />
          </div>

          {/* Right Section */}
          <div className="w-1/2 flex flex-col px-3">
            <div className="h-[100%] ml-auto overflow-y-auto w-full">
              <div className="border border-appGray-600 rounded-lg h-full flex flex-col px-2 pt-2 pb-5">
                <div className="rounded-lg p-2 flex-1">
                  <h6 className="font-semibold mb-1 text-base font-nunito">Uploaded files</h6>
                  {selectedFiles.length > 0 ? (
                    <div className="h-[180px] pn_scroller overflow-y-auto pr-1">
                      {selectedFiles.map((file, index) => (
                        <div key={index}>
                          {index !== 0 && <hr className="my-1 border-1 border-appGray-300" />}
                          <div className="flex justify-between items-center">
                            <p className="text-sm font-nunito">{file.name}</p>
                            <TrashIcon
                              className="cursor-pointer hover:text-red-500 transition"
                              width={20}
                              onClick={() => handleRemoveFile(index)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 text-center p-3 font-nunito mt-3">
                      No file uploaded yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <PrimaryButton
          onClick={handleSubmitCreateFolder}
          text={submittingAction ? "Uploading..." : "Upload"}
          loading={submittingAction}
        />

        {items.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-4">Your previously uploaded files</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-md overflow-hidden text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-4 py-2 border-b w-[100px]">#</th>
                    <th className="text-left px-4 py-2 border-b">Filename</th>
                    <th className="text-left px-4 py-2 border-b">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border-b">{index + 1}</td>
                      <td className="px-4 py-2 border-b">{item}</td>
                      <td className="px-4 py-2 border-b capitalize">
                        {item?.split(".")?.pop()?.toLowerCase() || "File"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <AppFooter />
    </>
  );
};

export default FileManager;
